import {
  PlayerHitter,
  PlayerPitcher,
  PlayerStatsHitter,
  PlayerStatsPitcher,
} from "@aura-frontend/omnibus-az-client";
import { toast } from "@win-reality/react-ui";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

import {
  CompetitionLevel,
  CompetitionLevelData,
} from "@/components/competition-level/competitionLevel";
import { usePlayerProfileStore } from "@/stores/player-profile";

import { calculateLastThirtyDaysDateRange } from "../utils/calculateDateRange";
import { useLatestGame } from "./hooks/useLatestGame";
import { usePlayerStats } from "./hooks/usePlayerStats";

export const CompetitionLevelCards = () => {
  const { id: playerId } = useParams() as { id: string };

  const playerType = usePlayerProfileStore((state) => state.playerType);
  const dateFrom = usePlayerProfileStore((state) => state.dateFrom);
  const dateTo = usePlayerProfileStore((state) => state.dateTo);
  const setDateFrom = usePlayerProfileStore((state) => state.setDateFrom);
  const setDateTo = usePlayerProfileStore((state) => state.setDateTo);

  const {
    data: latestGameData,
    isLoading: isLoadingLatestGameDefaultDate,
    error: latestGameDefaultDateError,
  } = useLatestGame({ playerId, playerType });

  const dateRange = calculateLastThirtyDaysDateRange(
    latestGameData?.latestGame?.game_started_at ?? null,
    latestGameData?.latestPractice?.game_started_at ?? null,
  );

  const hasSelectedDates = !!dateFrom && !!dateTo;

  useEffect(() => {
    // If the user has not selected dates, set the date range to the last 30 days
    if (!hasSelectedDates && dateRange) {
      setDateFrom(dateRange.startDate);
      setDateTo(dateRange.endDate);
    }
  }, [hasSelectedDates, dateRange, setDateFrom, setDateTo]);

  const {
    data: stats,
    isLoading: isLoadingPlayerStats,
    error: playerStatsError,
  } = usePlayerStats({
    playerId,
    startDate: hasSelectedDates ? dateFrom : (dateRange?.startDate ?? null),
    endDate: hasSelectedDates ? dateTo : (dateRange?.endDate ?? null),
    playerType: playerType,
  });

  const isLoading = isLoadingLatestGameDefaultDate || isLoadingPlayerStats;
  const hasError = !!latestGameDefaultDateError || !!playerStatsError;

  useEffect(() => {
    if (hasError) {
      toast(
        "Something went wrong while loading player information. Please try again.",
        { variant: "error" },
      );
    }
  }, [hasError]);

  const latestGame = latestGameData?.latestGame;
  const latestPractice = latestGameData?.latestPractice;

  const gameStats = stats?.gameStats;
  const practiceStats = stats?.practiceStats;
  const d1GameStats = stats?.d1GameStats;
  const d1PracticeStats = stats?.d1PracticeStats;

  const trainingVsCompetitionTitle =
    playerType === "hitter"
      ? "Training Vs D1 average"
      : "Bullpen Vs D1 average";

  const bpSessionComparisonTitle =
    playerType === "hitter"
      ? "BP Session Comparison"
      : "Last Bullpen Comparison";

  const vsCompetitionLevelLabels: CompetitionLevelData[] =
    playerType === "hitter"
      ? [
          {
            name: "Max Exit Velocity",
            values: [
              (gameStats as PlayerStatsHitter)?.max_exit_velocity,
              (d1GameStats as PlayerStatsHitter)?.max_exit_velocity,
            ],
          },
          {
            name: "AVG Exit Velocity",
            values: [
              (gameStats as PlayerStatsHitter)?.avg_exit_velocity,
              (d1GameStats as PlayerStatsHitter)?.avg_exit_velocity,
            ],
          },
          {
            name: "Chase Rate",
            values: [
              (gameStats as PlayerStatsHitter)?.rate_chase,
              (d1GameStats as PlayerStatsHitter)?.rate_chase,
            ],
          },
          {
            name: "Contact Rate",
            values: [
              (gameStats as PlayerStatsHitter)?.rate_contact,
              (d1GameStats as PlayerStatsHitter)?.rate_contact,
            ],
          },
          {
            name: "AVG EV vs FB",
            values: [
              (gameStats as PlayerStatsHitter)?.avg_ev_vs_fb,
              (d1GameStats as PlayerStatsHitter)?.avg_ev_vs_fb,
            ],
          },
          {
            name: "AVG EV vs OFF",
            values: [
              (gameStats as PlayerStatsHitter)?.avg_ev_vs_offspeed,
              (d1GameStats as PlayerStatsHitter)?.avg_ev_vs_offspeed,
            ],
          },
        ]
      : [
          {
            name: "Max FB Velocity",
            values: [
              (gameStats as PlayerStatsPitcher)?.max_fb_velocity,
              (d1GameStats as PlayerStatsPitcher)?.max_fb_velocity,
            ],
          },
          {
            name: "AVG EV Against",
            values: [
              (gameStats as PlayerStatsPitcher)?.avg_exit_velocity_against,
              (d1GameStats as PlayerStatsPitcher)?.avg_exit_velocity_against,
            ],
          },
          {
            name: "In Zone Rate",
            values: [
              (gameStats as PlayerStatsPitcher)?.rate_in_zone,
              (d1GameStats as PlayerStatsPitcher)?.rate_in_zone,
            ],
          },
          {
            name: "K Rate",
            values: [
              (gameStats as PlayerStatsPitcher)?.rate_k,
              (d1GameStats as PlayerStatsPitcher)?.rate_k,
            ],
          },
          {
            name: "Walk Rate",
            values: [
              (gameStats as PlayerStatsPitcher)?.rate_walk,
              (d1GameStats as PlayerStatsPitcher)?.rate_walk,
            ],
          },
          {
            name: "Whiff Rate",
            values: [
              (gameStats as PlayerStatsPitcher)?.rate_whiff,
              (d1GameStats as PlayerStatsPitcher)?.rate_whiff,
            ],
          },
        ];

  const trainingVsCompetitionLabels: CompetitionLevelData[] =
    playerType === "hitter"
      ? [
          {
            name: "Max Exit Velocity",
            values: [
              (practiceStats as PlayerStatsHitter)?.max_exit_velocity,
              (d1PracticeStats as PlayerStatsHitter)?.max_exit_velocity,
            ],
          },
          {
            name: "AVG Exit Velocity",
            values: [
              (practiceStats as PlayerStatsHitter)?.avg_exit_velocity,
              (d1PracticeStats as PlayerStatsHitter)?.avg_exit_velocity,
            ],
          },
          {
            name: "Max Projected Distance",
            values: [
              (practiceStats as PlayerStatsHitter)?.max_distance,
              (d1PracticeStats as PlayerStatsHitter)?.max_distance,
            ],
          },
          {
            name: "Average Projected Distance",
            values: [
              (practiceStats as PlayerStatsHitter)?.avg_distance,
              (d1PracticeStats as PlayerStatsHitter)?.avg_distance,
            ],
          },
          {
            name: "Average Launch",
            values: [
              (practiceStats as PlayerStatsHitter)?.avg_launch,
              (d1PracticeStats as PlayerStatsHitter)?.avg_launch,
            ],
          },
          {
            name: "Average Spin",
            values: [
              (practiceStats as PlayerStatsHitter)?.avg_spin,
              (d1PracticeStats as PlayerStatsHitter)?.avg_spin,
            ],
          },
        ]
      : [
          {
            name: "Max FB Velocity",
            values: [
              (practiceStats as PlayerStatsPitcher)?.max_fb_velocity,
              (d1PracticeStats as PlayerStatsPitcher)?.max_fb_velocity,
            ],
          },
          {
            name: "Max FB IVB",
            values: [
              (practiceStats as PlayerStatsPitcher)?.max_fb_ivb,
              (d1PracticeStats as PlayerStatsPitcher)?.max_fb_ivb,
            ],
          },
          {
            name: "Max FB Spin Rate",
            values: [
              (practiceStats as PlayerStatsPitcher)?.max_fb_spin_rate,
              (d1PracticeStats as PlayerStatsPitcher)?.max_fb_spin_rate,
            ],
          },
          {
            name: "AVG FB Velocity",
            values: [
              (practiceStats as PlayerStatsPitcher)?.avg_fb_velocity,
              (d1PracticeStats as PlayerStatsPitcher)?.avg_fb_velocity,
            ],
          },
          {
            name: "AVG FB Efficiency",
            values: [
              (practiceStats as PlayerStatsPitcher)?.avg_fb_efficiency,
              (d1PracticeStats as PlayerStatsPitcher)?.avg_fb_efficiency,
            ],
          },
          {
            name: "In Zone Rate",
            values: [
              (practiceStats as PlayerStatsPitcher)?.rate_in_zone,
              (d1PracticeStats as PlayerStatsPitcher)?.rate_in_zone,
            ],
          },
        ];

  const lastGameComparisonLabels: CompetitionLevelData[] =
    playerType === "hitter"
      ? [
          {
            name: "Max Exit Velocity",
            values: [
              (latestGame as PlayerHitter)?.max_exit_velocity,
              (gameStats as PlayerStatsHitter)?.max_exit_velocity,
            ],
          },
          {
            name: "AVG Exit Velocity",
            values: [
              (latestGame as PlayerHitter)?.avg_exit_velocity,
              (gameStats as PlayerStatsHitter)?.avg_exit_velocity,
            ],
          },
          {
            name: "Chase Rate",
            values: [
              (latestGame as PlayerHitter)?.rate_chase,
              (gameStats as PlayerStatsHitter)?.rate_chase,
            ],
          },
          {
            name: "Contact Rate",
            values: [
              (latestGame as PlayerHitter)?.rate_contact,
              (gameStats as PlayerStatsHitter)?.rate_contact,
            ],
          },
          {
            name: "AVG EV vs FB",
            values: [
              (latestGame as PlayerHitter)?.avg_ev_vs_fb,
              (gameStats as PlayerStatsHitter)?.avg_ev_vs_fb,
            ],
          },
          {
            name: "AVG EV vs OFF",
            values: [
              (latestGame as PlayerHitter)?.avg_ev_vs_offspeed,
              (gameStats as PlayerStatsHitter)?.avg_ev_vs_offspeed,
            ],
          },
        ]
      : [
          {
            name: "Max FB Velocity",
            values: [
              (latestGame as PlayerPitcher)?.max_fb_velocity,
              (gameStats as PlayerStatsPitcher)?.max_fb_velocity,
            ],
          },
          {
            name: "AVG EV Against",
            values: [
              (latestGame as PlayerPitcher)?.avg_exit_velocity_against,
              (gameStats as PlayerStatsPitcher)?.avg_exit_velocity_against,
            ],
          },
          {
            name: "In Zone Rate",
            values: [
              (latestGame as PlayerPitcher)?.rate_in_zone,
              (gameStats as PlayerStatsPitcher)?.rate_in_zone,
            ],
          },
          {
            name: "K Rate",
            values: [
              (latestGame as PlayerPitcher)?.rate_k,
              (gameStats as PlayerStatsPitcher)?.rate_k,
            ],
          },
          {
            name: "Walk Rate",
            values: [
              (latestGame as PlayerPitcher)?.rate_walk,
              (gameStats as PlayerStatsPitcher)?.rate_walk,
            ],
          },
          {
            name: "Whiff Rate",
            values: [
              (latestGame as PlayerPitcher)?.rate_whiff,
              (gameStats as PlayerStatsPitcher)?.rate_whiff,
            ],
          },
        ];

  const bpSessionComparisonLabels: CompetitionLevelData[] =
    playerType === "hitter"
      ? [
          {
            name: "Max Exit Velocity",
            values: [
              (latestPractice as PlayerHitter)?.max_exit_velocity,
              (practiceStats as PlayerStatsHitter)?.max_exit_velocity,
            ],
          },
          {
            name: "AVG Exit Velocity",
            values: [
              (latestPractice as PlayerHitter)?.avg_exit_velocity,
              (practiceStats as PlayerStatsHitter)?.avg_exit_velocity,
            ],
          },
          {
            name: "Max Projected Distance",
            values: [
              (latestPractice as PlayerHitter)?.max_distance,
              (practiceStats as PlayerStatsHitter)?.max_distance,
            ],
          },
          {
            name: "Average Projected Distance",
            values: [
              (latestPractice as PlayerHitter)?.avg_distance,
              (practiceStats as PlayerStatsHitter)?.avg_distance,
            ],
          },
          {
            name: "Average Launch",
            values: [
              (latestPractice as PlayerHitter)?.avg_launch,
              (practiceStats as PlayerStatsHitter)?.avg_launch,
            ],
          },
          {
            name: "Average Spin",
            values: [
              (latestPractice as PlayerHitter)?.avg_spin,
              (practiceStats as PlayerStatsHitter)?.avg_spin,
            ],
          },
        ]
      : [
          {
            name: "Max FB Velocity",
            values: [
              (latestPractice as PlayerPitcher)?.max_fb_velocity,
              (practiceStats as PlayerStatsPitcher)?.max_fb_velocity,
            ],
          },
          {
            name: "Max FB IVB",
            values: [
              (latestPractice as PlayerPitcher)?.max_fb_ivb,
              (practiceStats as PlayerStatsPitcher)?.max_fb_ivb,
            ],
          },
          {
            name: "Max FB Spin Rate",
            values: [
              (latestPractice as PlayerPitcher)?.max_fb_spin_rate,
              (practiceStats as PlayerStatsPitcher)?.max_fb_spin_rate,
            ],
          },
          {
            name: "AVG FB Velocity",
            values: [
              (latestPractice as PlayerPitcher)?.avg_fb_velocity,
              (practiceStats as PlayerStatsPitcher)?.avg_fb_velocity,
            ],
          },
          {
            name: "AVG FB Efficiency",
            values: [
              (latestPractice as PlayerPitcher)?.avg_fb_efficiency,
              (practiceStats as PlayerStatsPitcher)?.avg_fb_efficiency,
            ],
          },
          {
            name: "In Zone Rate",
            values: [
              (latestPractice as PlayerPitcher)?.rate_in_zone,
              (practiceStats as PlayerStatsPitcher)?.rate_in_zone,
            ],
          },
        ];

  return (
    <div className="grid lg:grid-cols-2 lg:grid-rows-2 grid-cols-1 grid-rows-1 gap-2 xl:col-span-4">
      <CompetitionLevel
        title="Vs D1 Average"
        columnNames={["Me", "D1 Avg"]}
        data={vsCompetitionLevelLabels}
        isLoading={isLoading}
      />

      <CompetitionLevel
        title={trainingVsCompetitionTitle}
        columnNames={["Me", "D1 Avg"]}
        data={trainingVsCompetitionLabels}
        isLoading={isLoading}
      />

      <CompetitionLevel
        title="Last Game Comparison"
        columnNames={["Last Game", "My Average"]}
        data={lastGameComparisonLabels}
        isLoading={isLoading}
      />

      <CompetitionLevel
        title={bpSessionComparisonTitle}
        columnNames={["Last Session", "My Average"]}
        data={bpSessionComparisonLabels}
        isLoading={isLoading}
      />
    </div>
  );
};
